/**
 * Restart of a recursive access-control job (S8 Task 8.11, ruling D14): a REPLACE (`set`) or REMOVE job asks for the
 * typed directory name again (the root's last segment; the file-system name for a root job); a MERGE (`modify`) job
 * asks one plain confirmation with the same copy (the app-level confirm dialog); the typed name is asked inline in the
 * panel (see `aclRestartConfirmRequest`). The copy restates what the change does and what a Restart protects —
 * `aclRecursiveGuaranteeLines` without a version (a Restart confirms a path, not a version; a file-system root job
 * promises no replacement detection — review fixes C #3). Review opus F2: it also shows WHAT a Restart applies — the
 * job's start time, every entry of its change and the change's mask / default-scope warnings — because a Restart of an
 * old job re-applies that change over whatever the paths hold now. Cancel sends nothing.
 */
import { aclChangeEntryLines, aclChangeWarnings } from '../../../core/engines/azureStorageAclChangeText';
import { ACL_FAILURES_LINE, aclRecursiveGuaranteeLines, aclRootIdentity, aclRootLabel } from '../../../core/engines/azureStorageAclJobText';
import type { TransferJob } from '../../../types/azureStorage';
import type { ConfirmDialogRequest } from '../../workspace/common/components/confirm/confirmDialogStore';
import { propagateTypedName } from '../../workspace/storage-explorer/hns/aclPropagate';

const MODE_WORDS = {
  set: (root: string) => `Restart replaces the access control list of ${root} and every file and directory under it with this job's ACL, from the beginning.`,
  modify: (root: string) => `Restart merges this job's entries into ${root} and every file and directory under it, from the beginning.`,
  remove: (root: string) => `Restart removes this job's named entries from ${root} and every file and directory under it, from the beginning.`,
} as const;

/** "2026-09-25 12:00 UTC" — the same text in every time zone. */
const startedWhen = (iso: string): string => `${iso.slice(0, 16).replace('T', ' ')} UTC`;

/** What a Restart of an `acl` job asks: the copy lines, and — for replace / remove — the typed directory name. */
export function aclRestartCopy(job: TransferJob): { typed: boolean; name: string; lines: string[] } | undefined {
  const acl = job.acl;
  if (job.kind !== 'acl' || !acl) return undefined;
  const root = aclRootLabel(acl);
  return {
    typed: acl.change.mode === 'set' || acl.change.mode === 'remove',
    name: propagateTypedName(acl.target),
    lines: [
      MODE_WORDS[acl.change.mode](root), `This job was started on ${startedWhen(job.startedAt)} with the entries below.`, ...aclChangeEntryLines(acl.change),
      ...aclChangeWarnings(acl.change, root), ...aclRecursiveGuaranteeLines({ root, rootIdentity: aclRootIdentity(acl.target) }), ACL_FAILURES_LINE,
    ],
  };
}

export const ACL_RESTART_TITLE = 'Restart the access-control job?';

/**
 * The plain confirmation of a MERGE job's Restart (the app-level confirm dialog; nothing to type). A replace / remove
 * Restart answers `undefined` here: its typed name is asked inline in the Transfers panel (`TypedNameConfirmPanel` —
 * the app-level dialog cannot take typing over the open panel).
 */
export function aclRestartConfirmRequest(job: TransferJob, restart: () => Promise<void>): ConfirmDialogRequest | undefined {
  const copy = aclRestartCopy(job);
  if (!copy || copy.typed) return undefined;
  return {
    title: ACL_RESTART_TITLE,
    description: <span className="grid gap-1" data-testid="transfer-acl-restart-copy">{copy.lines.map((line) => <span key={line}>{line}</span>)}</span>,
    confirmLabel: 'Restart',
    variant: 'default',
    onConfirm: restart,
  };
}
